import { IModalDialogOptions } from '../types/ui/modal-options.type';
import {
  EntityId,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  ROW_SELECTOR_ID,
  WIDGET_COMPONENT,
} from '../types';
import {
  size,
  gte,
  includes,
  find,
  set,
  isEqual,
  map,
  isEmpty,
  forEach,
  isNil,
} from 'lodash';
import { XuxGridListComponent } from '@app/components/xux/xux-grid-list/xux-grid-list-widget.component';
import { CustomSelectAllComponent } from '@app/components/xvi-grid/custom-select-all/custom-select-all.component';
import { WIDGET_FAMILY } from '@config/global-constants';
import { COMMON_GENERAL_MESSAGE_CLASS } from '../constants/common-warn-grid-setting';
import { getAllRowsAfterFilterAndSort } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';

/**
 * use this to get site selector max select limit
 *
 * @param maxSelectLimit only can select up to this limit
 * @param alreadySelectedSiteIds already selected site ids(will disable check box in site selector)
 */
const getSiteSelectorMaxLimitDefine = (
  maxSelectLimit: number,
  alreadySelectedSiteIds: EntityId[] = [],
  tips: string = '',
): IModalDialogOptions => {
  const alreadySelectedSiteCount = size(alreadySelectedSiteIds);
  // total site count limit
  let isMeetMaxSelectLimit = gte(alreadySelectedSiteCount, maxSelectLimit);

  // remain select limit
  const remainMaxSelectLimit = maxSelectLimit - alreadySelectedSiteCount;
  // selected site ids during this time pops up
  let newSelectedSiteIds = [];

  const judgeIsDataSelectable = (data: { id: EntityId }): boolean => {
    const notSelectedFirst = !includes(alreadySelectedSiteIds, data.id);
    const selectedNow = includes(newSelectedSiteIds, data.id);

    return isMeetMaxSelectLimit ? selectedNow : notSelectedFirst;
  };

  return {
    formRules: {
      fields: {
        tips: {
          widget: WIDGET_COMPONENT.label,
          widgetFamily: WIDGET_FAMILY.LEGACY,
          title: tips,
          widgetData: {
            itemClass: COMMON_GENERAL_MESSAGE_CLASS,
          },
          show: !isEmpty(tips),
        },
        items: {
          widgetData: {
            // not shown select all when meet max select limit
            multiselect: !isMeetMaxSelectLimit,
            userGridOptions: {
              isRowSelectable: ({ data }): boolean =>
                judgeIsDataSelectable(data),
              getRowClass: ({ data }): string =>
                includes(alreadySelectedSiteIds, data.id) ||
                (!includes(newSelectedSiteIds, data.id) && isMeetMaxSelectLimit)
                  ? 'row-disabled'
                  : '',
            },
            eventHandlers: {
              [XUX_GRID_ACTIONS.GRID_READY]: (
                _,
                grid: XuxGridListComponent,
              ): void => {
                const rowSelectorColumn = find(
                  grid.xuxGridComponent.columnDefinitions,
                  { colId: ROW_SELECTOR_ID },
                );

                if (isNil(rowSelectorColumn)) {
                  return;
                }

                if (!isMeetMaxSelectLimit) {
                  set(
                    rowSelectorColumn,
                    'headerComponentFramework',
                    CustomSelectAllComponent,
                  );
                  set(rowSelectorColumn, 'headerCheckboxSelection', false);
                  set(rowSelectorColumn, 'headerComponentParams', {
                    dataSource:
                      grid.xuxGridComponent?.userGridOptions?.datasource,
                    maxSelectLimit: remainMaxSelectLimit,
                  });
                } else {
                  set(
                    rowSelectorColumn,
                    'checkboxSelection',
                    ({ data }): boolean => {
                      return (
                        !isMeetMaxSelectLimit ||
                        includes(alreadySelectedSiteIds, data.id)
                      );
                    },
                  );
                }

                grid.xuxGridComponent.gridApi.setColumnDefs(
                  grid.xuxGridComponent.columnDefinitions,
                );
              },
              [XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED]: (
                action: XuxGridAction,
                grid: XuxGridListComponent,
              ): void => {
                const rowSelectorColumn = find(
                  grid.xuxGridComponent.columnDefinitions,
                  { colId: ROW_SELECTOR_ID },
                );

                if (isNil(rowSelectorColumn)) {
                  return;
                }

                newSelectedSiteIds = map(
                  grid.getSelectedData(action.payload),
                  'id',
                );
                const newSelectedSiteCount = size(newSelectedSiteIds);
                const isCurMeetTheMaxSiteCount = gte(
                  newSelectedSiteCount,
                  remainMaxSelectLimit,
                );
                const isMeetSiteChanged = !isEqual(
                  isMeetMaxSelectLimit,
                  isCurMeetTheMaxSiteCount,
                );

                if (isMeetSiteChanged) {
                  isMeetMaxSelectLimit = isCurMeetTheMaxSiteCount;

                  // hidden row check box
                  set(
                    rowSelectorColumn,
                    'checkboxSelection',
                    ({ data }): boolean => judgeIsDataSelectable(data),
                  );

                  grid.xuxGridComponent.gridApi.setColumnDefs(
                    grid.xuxGridComponent.columnDefinitions,
                  );

                  forEach(
                    getAllRowsAfterFilterAndSort(grid.xuxGridComponent.gridApi),
                    (node) => {
                      const nodeData = node.data;

                      node.setRowSelectable(judgeIsDataSelectable(nodeData));
                    },
                  );
                }
              },
            },
          },
        },
      },
    },
  };
};
export { getSiteSelectorMaxLimitDefine };
